import { Controller } from "@hotwired/stimulus"
import Swal from "sweetalert2"

const adminCountFormatter = new Intl.NumberFormat("en-CA", { maximumFractionDigits: 0 })
const adminAmountFormatter = new Intl.NumberFormat("en-CA", { minimumFractionDigits: 0, maximumFractionDigits: 2 })

export default class extends Controller {
    static values = {
        url: String,
        name: String,
        amount: Number, // cents
        email: String
    }

    pay(event) {
        event.preventDefault()

        const button = event.currentTarget
        const row = button.closest("tr")

        const url = this.urlValue
        const name = this.nameValue
        const amountCents = Math.round(this.amountValue || 0)
        const amount = adminAmountFormatter.format(amountCents / 100)
        const email = this.emailValue

        Swal.fire({
            title: "Send Interac payout?",
            html: `Send <strong>${amount} CAD</strong> to <strong>${name}</strong> (<code>${email}</code>)?`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, send payout",
            cancelButtonText: "Cancel",
            reverseButtons: true
        }).then(result => {
            if (!result.isConfirmed) return

            this.setLoading(button)

            fetch(url, {
                method: "POST",
                headers: {
                    "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content,
                    "Accept": "application/json"
                },
                credentials: "same-origin"
            })
                .then(response =>
                    response.json().then(body => ({ ok: response.ok, body }))
                )
                .then(({ ok, body }) => {
                    if (!ok) {
                        this.restoreButton(button)

                        Swal.fire({
                            title: "Wise error",
                            text: body.message || "The payout could not be sent.",
                            icon: "error"
                        })

                        return
                    }

                    Swal.fire({
                        title: "Payout sent",
                        text: body.message || "The Interac payout was sent successfully.",
                        icon: "success",
                        confirmButtonText: "OK"
                    }).then(() => {
                        this.removeRow(row)

                        this.updatePendingSummary(amountCents)
                        this.updatePaidSummary(amountCents)
                        this.updateWiseBalance(amountCents)
                        this.disableButtonsIfBalanceTooLow()
                        this.updateEmptyStateIfNeeded()
                    })
                })
                .catch(() => {
                    this.restoreButton(button)

                    Swal.fire({
                        title: "Error",
                        text: "Unexpected error while sending payout.",
                        icon: "error"
                    })
                })
        })
    }

    setLoading(button) {
        button.disabled = true
        button.dataset.originalHtml = button.innerHTML

        button.innerHTML = `
          <span class="inline-flex items-center gap-1">
            <span class="animate-pulse">Sending...</span>
          </span>
        `
    }

    restoreButton(button) {
        button.disabled = false

        if (button.dataset.originalHtml) {
            button.innerHTML = button.dataset.originalHtml
        }
    }

    removeRow(row) {
        if (!row) return

        row.style.transition = "opacity 180ms ease, transform 180ms ease"
        row.style.opacity = "0"
        row.style.transform = "translateX(12px)"

        window.setTimeout(() => {
            row.remove()
        }, 180)
    }

    updatePendingSummary(amountCents) {
        this.updateNumber("[data-interac-pending-count]", -1)
        this.updateCurrency("[data-interac-pending-total-cents]", -amountCents)
        this.updateNumber("[data-interac-showing-count]", -1)
    }

    updatePaidSummary(amountCents) {
        this.updateNumber("[data-interac-completed-count]", 1)
        this.updateCurrency("[data-interac-completed-total-cents]", amountCents)
    }

    updateWiseBalance(totalCostCents) {
        this.updateCurrency("[data-wise-balance-cents]", -totalCostCents)
    }

    disableButtonsIfBalanceTooLow() {
        const balanceElement = document.querySelector("[data-wise-balance-cents]")
        if (!balanceElement) return

        const remainingBalanceCents = parseInt(balanceElement.dataset.value || "0", 10)
        if (Number.isNaN(remainingBalanceCents)) return

        document.querySelectorAll("[data-controller~='interac-payout']").forEach(button => {
            if (button.disabled) return

            const amountCents = parseInt(button.dataset.interacPayoutAmountValue || "0", 10)
            if (amountCents > remainingBalanceCents) {
                button.disabled = true
                button.classList.remove("bg-primary", "hover:bg-sky-700", "text-white")
                button.classList.add("bg-slate-200", "text-slate-400", "cursor-not-allowed", "dark:bg-slate-800", "dark:text-slate-500")
                button.title = "Insufficient Wise balance to cover payout"
            }
        })
    }

    updateEmptyStateIfNeeded() {
        window.setTimeout(() => {
            const tbody = document.querySelector("[data-interac-payouts-tbody]")
            if (!tbody) return
            if (tbody.querySelector("tr")) return

            const tableWrapper = tbody.closest(".overflow-x-auto")
            if (!tableWrapper) return

            tableWrapper.outerHTML = `
              <div class="px-6 py-10 text-center">
                <p class="text-slate-500 dark:text-slate-400">No pending Interac payouts 🎉</p>
              </div>
            `
        }, 220)
    }

    updateNumber(selector, delta) {
        document.querySelectorAll(selector).forEach(element => {
            const current = parseInt(element.dataset.value || element.textContent, 10) || 0
            const next = Math.max(current + delta, 0)

            element.dataset.value = next
            element.textContent = adminCountFormatter.format(next)
        })
    }

    updateCurrency(selector, deltaCents) {
        document.querySelectorAll(selector).forEach(element => {
            const current = parseInt(element.dataset.value || "0", 10) || 0
            const next = Math.max(current + deltaCents, 0)

            element.dataset.value = next
            element.textContent = (next / 100).toLocaleString("en-CA", {
                style: "currency",
                currency: "CAD",
                minimumFractionDigits: 0,
                maximumFractionDigits: 2
            })
        })
    }
}
